import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row } from "react-bootstrap";
import { useResizeObserver } from "@reactuses/core";
import { Label } from "./fields";
import { Button } from "../../core/basic/Button";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { useFieldArray, useWatch } from "react-hook-form";
import { GridList, GridListItem, Button as AriaButton, useDragAndDrop } from "react-aria-components";
import { GrDrag, GrFormClose } from "react-icons/gr";
import RuleForm from "./RuleForm";
import HeaderFields from "./HeaderFields";
import { RULE_DEFAULTS } from "./constants";
import { useProgramContext } from "./ProgramContext";
import ActionForm from "./ActionForm";
import { LuCopyPlus } from "react-icons/lu";
import ConfirmationModal from "../../core/blocks/ConfirmationModal";
import HarvestingRuleForm from "./HarvestingRuleForm";
import { HoverInfo } from "../../core/basic/Tooltip";
import { css } from "../../../../styled-system/css";

const RULE_COMPONENTS = {
  harvesting: HarvestingRuleForm,
  default: RuleForm,
};

const RuleContent = ({ allowsDragging, control, index, remove, insert, fieldName, isReadOnly }) => {
  const { t } = useTranslation(["programs"]);
  const { engine } = useProgramContext();
  const [expanded, setExpanded] = useState(true);
  const [height, setHeight] = useState(0);
  const contentRef = useRef(null);

  useResizeObserver(contentRef, () => {
    if (contentRef.current) setHeight(contentRef.current.clientHeight + 20);
  });

  const ruleSpec = useWatch({
    control,
    name: `${fieldName}.${index}`,
  });

  const RuleComponent = RULE_COMPONENTS[engine] || RULE_COMPONENTS.default;

  return (
    <>
      <HStack gap="0">
        <HStack flex="3" gap="0">
          {allowsDragging && (
            <AriaButton
              slot="drag"
              className={css({
                fontSize: "14px",
                marginRight: "1rem",
                color: "grey",
                cursor: "grab",
                background: "none",
                border: "none",
                padding: 0,
                borderRadius: "sm",
                transition: "all 0.15s ease-in-out",
                _hover: {
                  color: "{colors.trellisPalette.3}",
                  transform: "scale(1.2)",
                  background: "trellisPalette.3/8",
                },
              })}
            >
              <GrDrag />
            </AriaButton>
          )}
          <span
            className={css({
              cursor: "pointer",
              color: "{colors.trellisPalette.3}",
              fontSize: "14px",
              fontWeight: "600",
              width: "60px",
            })}
            onClick={() => setExpanded(!expanded)}
          >
            {t(($) => $.programs.form["Rule {n}"], { n: index + 1 })}
          </span>
          <span className={css({ fontSize: "12px", color: "grey" })}>{ruleSpec?.comment}</span>
        </HStack>
        {!isReadOnly && engine !== "tagging" && (
          <HStack flex="1" justify="end" gap="0">
            <span
              className={css({
                cursor: "pointer",
                fontSize: "18px",
                color: "grey",
              })}
              onClick={() => {
                insert(index + 1, { ...ruleSpec, comment: (ruleSpec?.comment ?? "") + " (copy)" });
              }}
            >
              <LuCopyPlus />
            </span>
            <span
              className={css({
                cursor: "pointer",
                fontSize: "24px",
                color: "grey",
              })}
            >
              <ConfirmationModal
                title={t(($) => $.programs.form["Delete Rule"])}
                confirmationMessage={t(($) => $.programs.form["Are you sure you want to delete this rule?"])}
                onConfirm={() => remove(index)}
                inlineModal
                modalButtonText={<GrFormClose />}
                confirmButtonText={t(($) => $.programs.form.Delete)}
              />
            </span>
          </HStack>
        )}
      </HStack>
      <div
        style={{
          maxHeight: expanded ? height : "0",
          transition: "all 0.2s ease-in-out",
          overflow: expanded ? "unset" : "hidden",
          animation: "0.2s delay-overflow",
        }}
      >
        <div ref={contentRef}>
          <VStack alignItems="stretch" marginTop="medium" className="fs-standard">
            <RuleComponent control={control} fieldName={`${fieldName}.${index}`} isReadOnly={isReadOnly} />
          </VStack>
        </div>
      </div>
    </>
  );
};

const ProgramsForm = ({ control, formState, hideHeader, fieldName, isReadOnly }) => {
  const { t } = useTranslation(["programs"]);
  const fieldNamespace = (name) => (fieldName ? `${fieldName}.${name}` : name);
  const rulesPath = fieldNamespace("rules");

  const { fields, append, remove, insert, move } = useFieldArray({ control, name: rulesPath });
  const fieldIndexMap = new Map(fields.map((f, i) => [f.id, i]));

  const { engine } = useProgramContext();
  const listRef = useRef(null);

  const { dragAndDropHooks } = useDragAndDrop({
    isDisabled: isReadOnly,
    getItems: (keys) => [...keys].map((key) => ({ "text/plain": String(key) })),
    onDragStart(e) {
      const key = [...e.keys][0];
      const el = listRef.current?.querySelector(`[data-key="${key}"]`);
      if (el) {
        listRef.current.style.setProperty("--drag-item-height", `${el.offsetHeight}px`);
        requestAnimationFrame(() => {
          el.style.height = "0";
          el.style.overflow = "hidden";
          el.style.padding = "0";
          el.style.opacity = "0";
        });
      }
    },
    onDragEnd() {
      listRef.current?.querySelectorAll("[data-dragging]").forEach((el) => {
        el.style.height = "";
        el.style.overflow = "";
        el.style.padding = "";
        el.style.opacity = "";
      });
    },
    onReorder(e) {
      const fromKey = [...e.keys][0];
      const fromIndex = fieldIndexMap.get(fromKey) ?? -1;
      let toIndex = fieldIndexMap.get(e.target.key) ?? -1;
      if (e.target.dropPosition === "after") toIndex += 1;
      if (fromIndex < toIndex) toIndex -= 1;
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
      move(fromIndex, toIndex);
    },
  });

  return (
    <Row>
      {!hideHeader && (
        <HeaderFields control={control} formState={formState} fieldName={fieldName} isReadOnly={isReadOnly} />
      )}
      <VStack alignItems="stretch" marginTop="medium" width="100%">
        {engine === "bidding" && (
          <VStack alignItems="stretch" marginBottom="small">
            <div className={"fs-standard " + css({ paddingBlock: "small" })}>
              <strong>{t(($) => $.programs.form["New Keyword/Target Action:"])}</strong>{" "}
              <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                {t(
                  ($) =>
                    $.programs.form["Perform this action when a new keyword or target is added to the media plan."],
                )}
              </HoverInfo>
            </div>

            <ActionForm
              fieldName={fieldNamespace("initial_action")}
              control={control}
              initialAction={true}
              isReadOnly={isReadOnly}
            />
          </VStack>
        )}
        {engine === "lineitem_bidding" && (
          <VStack alignItems="stretch" marginBottom="small">
            <div className={"fs-standard " + css({ paddingBlock: "small" })}>
              <strong>{t(($) => $.programs.form["New Lineitem Actions:"])}</strong>{" "}
              <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                {t(($) => $.programs.form["Perform this action when a new line item is added to the Order."])}
              </HoverInfo>
            </div>

            <ActionForm
              fieldName={fieldNamespace(`initial_action`)}
              control={control}
              initialAction={true}
              isReadOnly={isReadOnly}
            />
          </VStack>
        )}
        <Label>{t(($) => $.programs.form.Rules)}</Label>
        <GridList
          ref={listRef}
          aria-label={t(($) => $.programs.form.Rules)}
          items={fields}
          dragAndDropHooks={dragAndDropHooks}
          selectionMode="none"
          disallowTypeAhead
          className={css({
            display: "grid",
            gridTemplateColumns: "auto",
            paddingLeft: 0,
            listStyle: "none",
            "& .react-aria-DropIndicator": {
              display: "none",
              "&[data-drop-target]": {
                display: "block",
                minHeight: "var(--drag-item-height, 3rem)",
                outline: "2px solid {colors.trellisPalette.3}",
                outlineOffset: "-1px",
                background: "trellisPalette.3/10",
                borderRadius: "sm",
              },
            },
          })}
        >
          {(item) => {
            const index = fieldIndexMap.get(item.id);
            return (
              <GridListItem
                id={item.id}
                textValue={t(($) => $.programs.form["Rule {n}"], { n: index + 1 })}
                className={
                  "rule card p-4 " +
                  css({
                    transition: "all 0.2s ease-in-out",
                    marginBottom: "2.5rem",
                  })
                }
              >
                {({ allowsDragging }) => (
                  <RuleContent
                    allowsDragging={allowsDragging}
                    fieldName={fieldNamespace("rules")}
                    index={index}
                    remove={remove}
                    insert={insert}
                    control={control}
                    isReadOnly={isReadOnly}
                  />
                )}
              </GridListItem>
            );
          }}
        </GridList>
        <HStack justify="end">
          {formState.errors?.[fieldName]?.rules && (
            <span className="text-danger">{formState.errors?.[fieldName]?.rules?.root?.message}</span>
          )}
          {formState.errors?.rules && <span className="text-danger">{formState.errors?.rules?.root?.message}</span>}
          {!isReadOnly && (
            <Button
              onPress={() => {
                append(RULE_DEFAULTS[engine]);
              }}
            >
              {t(($) => $.programs.form["Add Rule"])}
            </Button>
          )}
        </HStack>
      </VStack>
    </Row>
  );
};

export default ProgramsForm;
